"use client"

import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSlug from 'rehype-slug'
import { supabase } from '@/lib/supabase'
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  Facebook, 
  Instagram,
  Linkedin,
  Link as LinkIcon,
  Check,
  CalendarPlus
} from 'lucide-react'
import { trackEvent } from '@/lib/analytics'

interface BlogPost {
  id: string
  title: string
  slug: string
  excerpt: string
  content: string
  image_url: string
  image_alt?: string
  category: string | null
  tags: string[]
  featured: boolean
  created_at: string
  updated_at: string
  reading_time_minutes: number
}

interface RelatedPost {
  id: string
  title: string
  slug: string
  excerpt: string
  image_url: string
  category: string
  created_at: string
}

// Placeholder post for development
const placeholderPost: BlogPost = {
  id: '1',
  title: 'Understanding Your Menstrual Cycle',
  slug: 'understanding-menstrual-cycle',
  excerpt: 'A deep dive into the phases of your cycle and how Chinese medicine can support hormonal balance.',
  content: `
## The Four Phases of Your Cycle

Understanding your menstrual cycle is fundamental to understanding your overall health as a woman. In Chinese medicine, we view the menstrual cycle as a beautiful reflection of the body's yin and yang energies in constant flow and transformation.

### Phase 1: Menstruation (Days 1-5)

The first day of your period marks day one of your cycle. During this time, the uterine lining sheds, and blood flows. In Chinese medicine, this is a time of movement and release.

**What's happening:**
- The uterine lining is shedding
- Hormone levels are at their lowest
- Energy may feel lower than usual

**How to support yourself:**
- Rest when possible
- Keep warm, especially your lower abdomen
- Avoid cold foods and drinks
- Gentle movement like walking or yin yoga

### Phase 2: Follicular Phase (Days 6-13)

After menstruation, your body begins building up the uterine lining again. Estrogen starts to rise, and you may notice increased energy and mood improvements.

> "The follicular phase is like springtime in your body - a time of renewal and growth." - Dr. Renee Knott

**Key points:**
- Energy begins to build
- Cervical fluid changes
- Great time for creative projects
- Ideal for more vigorous exercise

### Phase 3: Ovulation (Around Day 14)

Ovulation is the midpoint of your cycle when an egg is released. This is when fertility peaks and many women feel their best.

**Signs of ovulation:**
1. Clear, stretchy cervical mucus
2. Slight rise in basal body temperature
3. Increased libido
4. Peak energy and confidence

### Phase 4: Luteal Phase (Days 15-28)

After ovulation, progesterone rises to prepare the body for potential pregnancy. If pregnancy doesn't occur, hormone levels drop, triggering menstruation.

**Common experiences:**
- PMS symptoms may appear
- Appetite changes
- Need for more rest
- Emotional sensitivity

## How Chinese Medicine Supports Your Cycle

Acupuncture and herbal medicine can be incredibly supportive for regulating your menstrual cycle and addressing common concerns like:

- **Irregular periods** - restoring regular rhythm
- **Painful periods** - reducing cramping and discomfort  
- **Heavy bleeding** - supporting healthy flow
- **PMS symptoms** - balancing mood and physical symptoms
- **PCOS and endometriosis** - comprehensive support

### The Role of Acupuncture

Acupuncture works by:
- Improving blood flow to the reproductive organs
- Regulating the hypothalamic-pituitary-ovarian axis
- Reducing inflammation
- Calming the nervous system
- Supporting hormone balance

### Herbal Support

Chinese herbal medicine offers gentle yet effective formulas tailored to each phase of your cycle. These formulas are customized based on your unique constitution and symptoms.

---

*If you're experiencing menstrual irregularities or would like support with your cycle, [book a consultation](/appointments) with Dr. Renee Knott to discuss how Chinese medicine can help.*
`,
  image_url: 'https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=1200&q=80',
  category: 'Womens Health',
  tags: ['hormones', 'menstrual health', 'acupuncture', 'chinese medicine'],
  featured: true,
  created_at: '2025-01-05T00:00:00Z',
  updated_at: '2025-01-05T00:00:00Z',
  reading_time_minutes: 5
}

const placeholderRelated: RelatedPost[] = [
  {
    id: '2',
    title: 'Acupuncture for Fertility: What to Expect',
    slug: 'acupuncture-fertility-what-to-expect',
    excerpt: 'Everything you need to know about fertility acupuncture treatments.',
    image_url: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80',
    category: 'Fertility',
    created_at: '2025-01-03T00:00:00Z'
  },
  {
    id: '4',
    title: 'Managing Pregnancy Symptoms Naturally',
    slug: 'managing-pregnancy-symptoms-naturally',
    excerpt: 'Gentle and effective approaches to common pregnancy discomforts.',
    image_url: 'https://images.unsplash.com/photo-1493894473891-10fc1e5dbd22?w=800&q=80',
    category: 'Pregnancy',
    created_at: '2024-12-28T00:00:00Z'
  },
  {
    id: '3',
    title: 'Spring Wellness with Chinese Herbs',
    slug: 'spring-wellness-chinese-herbs',
    excerpt: 'Seasonal tips for maintaining balance and vitality.',
    image_url: 'https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=800&q=80',
    category: 'Herbal Medicine',
    created_at: '2025-01-01T00:00:00Z'
  }
]

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

function PostSkeleton() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Hero Skeleton */}
      <div className="relative h-[60vh] min-h-[400px]">
        <Skeleton className="absolute inset-0" />
      </div>
      
      {/* Content Skeleton */}
      <div className="container mx-auto px-4 -mt-20 relative z-10">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <Skeleton className="h-8 w-3/4 mb-4" />
          <Skeleton className="h-4 w-1/2 mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [post, setPost] = useState<BlogPost | null>(null)
  const [relatedPosts, setRelatedPosts] = useState<RelatedPost[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [usePlaceholder, setUsePlaceholder] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    async function fetchPost() {
      if (!slug) return
      
      setIsLoading(true)
      
      try {
        // Fetch the post
        const { data: postData, error: postError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('slug', slug)
          .eq('published', true)
          .single()
        
        if (postError || !postData) {
          // Check if this is a placeholder post
          if (slug === placeholderPost.slug) {
            setPost(placeholderPost)
            setRelatedPosts(placeholderRelated)
            setUsePlaceholder(true)
          } else {
            // Post not found
            navigate('/blog', { replace: true })
            return
          }
        } else {
          setPost(postData)
          
          // Fetch related posts (same category, different post)
          const { data: relatedData } = await supabase
            .from('blog_posts')
            .select('id, title, slug, excerpt, image_url, category, created_at')
            .eq('published', true)
            .eq('category', postData.category)
            .neq('id', postData.id)
            .limit(3)
          
          if (relatedData) {
            setRelatedPosts(relatedData)
          }
        }
      } catch (err) {
        console.error('Error fetching post:', err)
        // Use placeholder for demo
        if (slug === placeholderPost.slug) {
          setPost(placeholderPost)
          setRelatedPosts(placeholderRelated)
          setUsePlaceholder(true)
        } else {
          navigate('/blog', { replace: true })
        }
      }
      
      setIsLoading(false)
    }

    fetchPost()
  }, [slug, navigate])

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const shareUrl = typeof window !== 'undefined' ? window.location.href : ''

  if (isLoading) {
    return <PostSkeleton />
  }

  if (!post) {
    return null
  }

  return (
    <main className="min-h-screen bg-[#FDFBF7]">
      <Helmet>
        <title>{post.title} | Raintree Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={post.image_url} />
        <meta property="og:url" content={`https://raintreechinesemedicine.com.au/blog/${post.slug}`} />
        <meta property="article:published_time" content={post.created_at} />
        <meta property="article:modified_time" content={post.updated_at} />
        {post.category && <meta property="article:section" content={post.category} />}
        {post.tags?.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.image_url} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.excerpt,
            "image": post.image_url,
            "author": {
              "@type": "Person",
              "name": "Dr Renee Knott",
              "url": "https://raintreechinesemedicine.com.au/dr-renee-knott"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Raintree Chinese Medicine",
              "logo": {
                "@type": "ImageObject",
                "url": "https://raintreechinesemedicine.com.au/logo.png"
              }
            },
            "datePublished": post.created_at,
            "dateModified": post.updated_at,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://raintreechinesemedicine.com.au/blog/${post.slug}`
            },
            "articleSection": post.category || undefined,
            "keywords": post.tags?.join(", ")
          })}
        </script>
      </Helmet>

      {/* Hero Image */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative h-[60vh] min-h-[400px] max-h-[600px]"
      >
        <img
          src={post.image_url}
          alt={post.image_alt || post.title}
          width={1200}
          height={630}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        
        {/* Back Button */}
        <div className="absolute top-24 left-0 right-0">
          <div className="container mx-auto px-4">
            <Link 
              to="/blog"
              className="inline-flex items-center gap-2 text-white/90 hover:text-white transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span>Back to Blog</span>
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Content Card */}
      <div className="container mx-auto px-4 -mt-24 relative z-10 pb-16 mb-16">
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-3xl mx-auto"
        >
          {/* Main Content Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Placeholder Notice */}
            {usePlaceholder && (
              <div className="bg-[#DBB981]/10 border-b border-[#DBB981]/30 px-8 py-3">
                <p className="text-sm text-[rgb(132,137,116)] text-center">
                  This is a sample article. Connect your CMS to add real blog posts.
                </p>
              </div>
            )}
            
            <div className="p-8 md:p-12">
              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                {post.category && (
                  <Badge className="bg-[rgb(132,137,116)] hover:bg-[rgb(132,137,116)]/90">
                    {post.category}
                  </Badge>
                )}
                <span className="flex items-center gap-1.5 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post.created_at)}
                </span>
                <span className="flex items-center gap-1.5 text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  {post.reading_time_minutes} min read
                </span>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-3xl lg:text-4xl font-light text-[#231f20] leading-tight mb-6">
                {post.title}
              </h1>

              {/* Excerpt */}
              <p className="text-lg text-gray-600 leading-relaxed mb-8 pb-8 border-b border-gray-100">
                {post.excerpt}
              </p>

              {/* Content */}
              <div className="prose prose-sm max-w-none prose-headings:font-normal prose-headings:text-[rgb(132,137,116)] prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3 prose-h3:text-base prose-h3:mt-6 prose-h3:mb-2 prose-h4:text-sm prose-h4:font-medium prose-p:text-gray-600 prose-p:leading-relaxed prose-p:text-sm prose-a:text-[rgb(132,137,116)] prose-a:no-underline hover:prose-a:text-[#DBB981] prose-strong:text-[#231f20] prose-li:text-gray-600 prose-li:text-sm">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSlug]}
                >
                  {post.content}
                </ReactMarkdown>
              </div>

              {/* Related What We Help With */}
              {(() => {
                // Check category and tags for keyword matches
                const searchText = [
                  post.category?.toLowerCase() || '',
                  ...(post.tags?.map(t => t.toLowerCase()) || [])
                ].join(' ')
                
                const matchesFertility = searchText.includes('fertility') || searchText.includes('preconception') || searchText.includes('ivf') || searchText.includes('conception')
                const matchesPregnancy = searchText.includes('pregnancy') || searchText.includes('postpartum') || searchText.includes('prenatal') || searchText.includes('postnatal') || searchText.includes('birth')
                const matchesWomensHealth = searchText.includes('women') || searchText.includes('menstrual') || searchText.includes('hormonal') || searchText.includes('period') || searchText.includes('pms') || searchText.includes('pcos') || searchText.includes('endometriosis') || searchText.includes('menopause')
                const matchesStressSleep = searchText.includes('stress') || searchText.includes('sleep') || searchText.includes('digest') || searchText.includes('insomnia') || searchText.includes('anxiety') || searchText.includes('wellness') || searchText.includes('relaxation')
                
                const hasMatch = matchesFertility || matchesPregnancy || matchesWomensHealth || matchesStressSleep
                
                if (!hasMatch && !post.category) return null
                
                return (
                  <div className="mt-12 pt-8 border-t border-gray-100">
                    <div className="p-6 bg-[#FDFBF7] rounded-xl">
                      <p className="text-sm text-gray-600 mb-3">Interested in learning more?</p>
                      {matchesFertility && (
                        <Link 
                          to="/what-we-help-with/fertility-preconception"
                          className="inline-flex items-center gap-2 text-[rgb(132,137,116)] hover:text-[#DBB981] font-medium transition-colors"
                        >
                          Learn more about our Fertility & Preconception services →
                        </Link>
                      )}
                      {matchesPregnancy && !matchesFertility && (
                        <Link 
                          to="/what-we-help-with/pregnancy-postpartum"
                          className="inline-flex items-center gap-2 text-[rgb(132,137,116)] hover:text-[#DBB981] font-medium transition-colors"
                        >
                          Learn more about our Pregnancy & Postpartum services →
                        </Link>
                      )}
                      {matchesWomensHealth && !matchesFertility && !matchesPregnancy && (
                        <Link 
                          to="/what-we-help-with/menstrual-hormonal-health"
                          className="inline-flex items-center gap-2 text-[rgb(132,137,116)] hover:text-[#DBB981] font-medium transition-colors"
                        >
                          Learn more about Menstrual & Hormonal Health →
                        </Link>
                      )}
                      {matchesStressSleep && !matchesFertility && !matchesPregnancy && !matchesWomensHealth && (
                        <Link 
                          to="/what-we-help-with/stress-sleep-digestion"
                          className="inline-flex items-center gap-2 text-[rgb(132,137,116)] hover:text-[#DBB981] font-medium transition-colors"
                        >
                          Learn more about Stress, Sleep & Digestion support →
                        </Link>
                      )}
                      {!hasMatch && (
                        <Link 
                          to="/what-we-help-with"
                          className="inline-flex items-center gap-2 text-[rgb(132,137,116)] hover:text-[#DBB981] font-medium transition-colors"
                        >
                          Explore all our services →
                        </Link>
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-8">
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/blog/tag/${encodeURIComponent(tag.toLowerCase().replace(/\s+/g, '-'))}`}
                      >
                        <Badge 
                          variant="secondary"
                          className="bg-gray-100 text-gray-600 hover:bg-[rgb(132,137,116)] hover:text-white cursor-pointer transition-colors"
                        >
                          #{tag}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Share Section */}
              <div className="mt-8 pt-8 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Share this article</span>
                  <div className="flex items-center gap-2">
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-[#1877F2] hover:text-white transition-colors"
                      aria-label="Share on Facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a
                      href="https://www.instagram.com/raintreechinesemedicine/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#E1306C] hover:to-[#F77737] hover:text-white transition-colors"
                      aria-label="Follow on Instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a
                      href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-[#0A66C2] hover:text-white transition-colors"
                      aria-label="Share on LinkedIn"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                    <button
                      onClick={handleCopyLink}
                      className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-[rgb(132,137,116)] hover:text-white transition-colors"
                      aria-label="Copy link"
                    >
                      {copied ? <Check className="w-5 h-5" /> : <LinkIcon className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          </motion.article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="max-w-5xl mx-auto mt-16"
          >
            <h2 className="text-2xl font-light text-[rgb(132,137,116)] mb-8 text-center">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={relatedPost.image_url}
                      alt={relatedPost.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <Badge variant="secondary" className="mb-2 text-xs">
                      {relatedPost.category}
                    </Badge>
                    <h3 className="font-medium text-[#231f20] group-hover:text-[rgb(132,137,116)] transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </motion.section>
        )}
      </div>

      {/* Full-width CTA Section */}
      <section className="bg-[rgb(132,137,116)] py-12 lg:py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl lg:text-4xl font-normal text-white mb-8">
              Are you ready to begin your journey to better health?
            </h2>
            <p className="text-white/90 mb-8 text-lg">
              Book an appointment or a free 15-minute phone consult to talk through your needs with Renee and see if Chinese Medicine and acupuncture feel right for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button 
                className="w-full sm:w-auto rounded-full px-8 bg-white text-gray-600 hover:bg-white/80" 
                onClick={() => {
                  try { trackEvent('book_appointment_click', { location: 'blog_post_cta' }) } catch {}
                  if (typeof window !== 'undefined' && (window as any).openBookingModal) {
                    (window as any).openBookingModal('blog_post_cta')
                  }
                }}
                aria-label="Book Appointment"
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Book Appointment
                </span>
              </Button>
              <Button 
                variant="outline" 
                className="w-full sm:w-auto rounded-full border-2 border-white text-white hover:bg-white hover:text-gray-600" 
                onClick={() => {
                  try { trackEvent('book_appointment_click', { location: 'blog_post_free_consult' }) } catch {}
                  if (typeof window !== 'undefined' && (window as any).openBookingModal) {
                    (window as any).openBookingModal('blog_post_free_consult')
                  }
                }}
                aria-label="Free 15min Phone Consult"
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Free 15min Phone Consult 
                </span>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  )
}

